export interface ReadingLocator {
  episodeId: string;
  paragraphIndex: number;
  charOffset: number;
}

/**
 * 컨테이너 내 보이는 영역에서 가장 왼쪽 상단의 문자 위치를 반환합니다.
 * @param container - 스크롤 컨테이너 요소
 * @returns 보이는 첫 문자의 locator 또는 null
 */
export const getVisibleReadingLocator = (container: HTMLElement): ReadingLocator | null => {
  const containerRect = container.getBoundingClientRect();
  const visibleLeft = containerRect.left;
  const visibleRight = containerRect.right;
  const visibleTop = containerRect.top;
  const visibleBottom = containerRect.bottom;

  const allChars = container.querySelectorAll<HTMLElement>('[data-reading-char="true"]');
  const visibleChars = Array.from(allChars).filter((el) => {
    const rect = el.getBoundingClientRect();
    return (
      rect.right > visibleLeft &&
      rect.left < visibleRight &&
      rect.bottom > visibleTop &&
      rect.top < visibleBottom
    );
  });

  if (visibleChars.length === 0) return null;

  visibleChars.sort((a, b) => {
    const rectA = a.getBoundingClientRect();
    const rectB = b.getBoundingClientRect();
    const topDiff = rectA.top - rectB.top;
    if (Math.abs(topDiff) > 1) return topDiff;
    return rectA.left - rectB.left;
  });

  const charEl = visibleChars[0];
  const epId = charEl.dataset.sceneEpisodeId;
  const pIdx = charEl.dataset.paragraphIndex;
  const charOffset = charEl.dataset.charOffset;

  if (
    epId === undefined ||
    epId === null ||
    pIdx === undefined ||
    pIdx === null ||
    charOffset === undefined ||
    charOffset === null
  ) {
    return null;
  }

  return {
    episodeId: epId,
    paragraphIndex: Number(pIdx),
    charOffset: Number(charOffset)
  };
};

export interface ReadingProgress {
  currentIndex: number;
  totalChars: number;
  episodeId?: string;
}

/**
 * 컨테이너 내 보이는 영역에서 읽기 순서상 가장 뒤에 있는 글자의 인덱스와 전체 글자 수를 반환합니다.
 * @param container - 스크롤 컨테이너 요소
 * @returns { currentIndex, totalChars } 또는 null
 */
export const getReadingProgress = (container: HTMLElement): ReadingProgress | null => {
  const allChars = container.querySelectorAll<HTMLElement>('[data-reading-char="true"]');
  if (allChars.length === 0) return null;

  const containerRect = container.getBoundingClientRect();
  let lastVisibleIndex = -1;
  let lastVisibleEpisodeId: string | undefined;

  for (let i = 0; i < allChars.length; i++) {
    const rect = allChars[i].getBoundingClientRect();
    if (
      rect.right > containerRect.left &&
      rect.left < containerRect.right &&
      rect.bottom > containerRect.top &&
      rect.top < containerRect.bottom
    ) {
      lastVisibleIndex = i;
      const epId = allChars[i].dataset.sceneEpisodeId;
      if (epId != null) lastVisibleEpisodeId = epId;
    }
  }

  if (lastVisibleIndex < 0) return null;

  return {
    currentIndex: lastVisibleIndex,
    totalChars: allChars.length,
    ...(lastVisibleEpisodeId != null && { episodeId: lastVisibleEpisodeId })
  };
};
